import { Search, Download, Landmark, Eraser, RotateCw, FileOutput, MessageSquareOff } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TagBadge } from '@/components/shared/TagBadge';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import { t } from '@/i18n';
import type {
  MppFilter,
  DateFilterType,
  InvoiceStatus,
  InvoiceSource,
  ReimbursementFilter,
  Tag as TagType,
  ProjectOption,
} from '@ip/shared-types';

interface InvoiceFilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  mppFilter: MppFilter;
  onMppChange: (v: MppFilter) => void;
  dateType: DateFilterType;
  onDateTypeChange: (v: DateFilterType) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (v: DateRange | undefined) => void;
  statusFilter: InvoiceStatus | 'all';
  onStatusChange: (v: InvoiceStatus | 'all') => void;
  sourceFilter: InvoiceSource | 'all';
  onSourceChange: (v: InvoiceSource | 'all') => void;
  /** "Bez opisu" chip — only invoices without accounting notes. */
  noNotesOnly: boolean;
  onNoNotesChange: (v: boolean) => void;
  /** Zwroty pracownicze — active-case presence or a concrete status. */
  reimbursementFilter: ReimbursementFilter;
  onReimbursementChange: (v: ReimbursementFilter) => void;
  /** Project filter (Task B3) — hidden when the user can't list projects. */
  projects: ProjectOption[];
  projectFilter: string;
  onProjectChange: (v: string) => void;
  tags: TagType[];
  selectedTags: string[];
  onTagToggle: (tagId: string) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onRefresh: () => void;
  onExport: (key: 'pdf' | 'mbank') => void;
}

const MPP_LABELS: Record<MppFilter, string> = {
  all: 'invoices.filters.mpp.all',
  mpp_only: 'invoices.filters.mpp.mppOnly',
  without_mpp: 'invoices.filters.mpp.withoutMpp',
};

const REIMBURSEMENT_OPTIONS: ReimbursementFilter[] = [
  'all',
  'with',
  'pending_approval',
  'approved',
  'exported',
  'without',
];

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground mb-1 text-xs">{label}</p>
      {children}
    </div>
  );
}

export function InvoiceFilterBar(props: InvoiceFilterBarProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          placeholder={t('invoices.searchPlaceholder')}
          value={props.search}
          onChange={(e) => props.onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card className="py-2">
        <CardContent className="px-3">
          <div className="flex flex-wrap items-end gap-3">
            <FilterGroup label="MPP">
              <Select
                value={props.mppFilter}
                onValueChange={(v) => props.onMppChange(v as MppFilter)}
              >
                <SelectTrigger size="sm" className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['all', 'mpp_only', 'without_mpp'] as MppFilter[]).map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(MPP_LABELS[value])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterGroup>

            <FilterGroup label={t('invoices.filters.dateType.label')}>
              <div className="flex items-center gap-1">
                <Select
                  value={props.dateType}
                  onValueChange={(v) => props.onDateTypeChange(v as DateFilterType)}
                >
                  <SelectTrigger size="sm" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="issue_date">{t('invoices.filters.dateType.issueDate')}</SelectItem>
                    <SelectItem value="sale_date">{t('invoices.filters.dateType.saleDate')}</SelectItem>
                    <SelectItem value="payment_date">{t('invoices.filters.dateType.paymentDate')}</SelectItem>
                    <SelectItem value="created_at">{t('invoices.filters.dateType.createdAt')}</SelectItem>
                  </SelectContent>
                </Select>
                <DateRangePicker value={props.dateRange} onChange={props.onDateRangeChange} />
              </div>
            </FilterGroup>

            <FilterGroup label={t('invoices.columns.status')}>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={props.statusFilter}
                onValueChange={(v) => v && props.onStatusChange(v as InvoiceStatus | 'all')}
              >
                {(['all', 'paid', 'unpaid', 'exported'] as const).map((value) => (
                  <ToggleGroupItem key={value} value={value} className="px-3">
                    {t(`invoices.filters.status.${value}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </FilterGroup>

            <FilterGroup label={t('invoices.columns.source')}>
              <Select
                value={props.sourceFilter}
                onValueChange={(v) => props.onSourceChange(v as InvoiceSource | 'all')}
              >
                <SelectTrigger size="sm" className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="email">{t('invoices.source.email')}</SelectItem>
                  <SelectItem value="upload">{t('invoices.source.upload')}</SelectItem>
                  <SelectItem value="ksef">{t('invoices.source.ksef')}</SelectItem>
                </SelectContent>
              </Select>
            </FilterGroup>

            {props.projects.length > 0 && (
              <FilterGroup label={t('invoices.columns.project')}>
                <Select value={props.projectFilter} onValueChange={props.onProjectChange}>
                  <SelectTrigger size="sm" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('invoices.filters.projectAll')}</SelectItem>
                    {props.projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block size-2 shrink-0 rounded-full border"
                            style={{ backgroundColor: project.color ?? 'transparent' }}
                            aria-hidden="true"
                          />
                          <span className="truncate">
                            {project.name}
                            {project.status === 'closed'
                              ? ` ${t('projects.selector.closedSuffix')}`
                              : ''}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterGroup>
            )}

            <FilterGroup label={t('invoices.filters.reimbursement.label')}>
              <Select
                value={props.reimbursementFilter}
                onValueChange={(v) => props.onReimbursementChange(v as ReimbursementFilter)}
              >
                <SelectTrigger size="sm" className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REIMBURSEMENT_OPTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`invoices.filters.reimbursement.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterGroup>

            <FilterGroup label={t('invoices.columns.notes')}>
              <Toggle
                variant="outline"
                size="sm"
                pressed={props.noNotesOnly}
                onPressedChange={props.onNoNotesChange}
                aria-label={t('invoices.filters.noNotes')}
                className="px-3"
              >
                <MessageSquareOff className="size-4" />
                {t('invoices.filters.noNotes')}
              </Toggle>
            </FilterGroup>

            <div className="ml-auto flex items-center gap-1.5">
              {props.hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={props.onClearFilters}>
                  <Eraser className="size-4" />
                  {t('invoices.filters.clearAll')}
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon-sm" aria-label={t('invoices.filters.refresh')} onClick={props.onRefresh}>
                    <RotateCw className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('invoices.filters.refresh')}</TooltipContent>
              </Tooltip>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FileOutput className="size-4" />
                    {t('common.exportTo')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => props.onExport('pdf')}>
                    <Download className="size-4" /> {t('export.formats.pdf')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => props.onExport('mbank')}>
                    <Landmark className="size-4" /> mBank
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {props.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground mr-1 text-xs">
                {t('invoices.columns.tags')}:
              </span>
              {props.tags.map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color}
                  selected={props.selectedTags.includes(tag.id)}
                  onClick={() => props.onTagToggle(tag.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
